import type { AnalyticsFilter } from './AnalyticsTypes';

/**
 * Shared operations over `AnalyticsFilter[]` — the field→values filter shape used
 * by the global and page filter controllers, the analytics gateway, and every
 * view model that caches results by filter set. Keeping these in one place avoids
 * the merge/equality/serialize logic drifting across copies.
 */

/** A stable cache key for a filter set: order-insensitive over both fields and values. */
export function serializeAnalyticsFilters(filters: AnalyticsFilter[]): string {
    return filters
        .map((filter) => `${filter.field}=${[...filter.values].sort().join('|')}`)
        .sort()
        .join(';');
}

export interface FilterMergeResult {
    filters: AnalyticsFilter[];
    changed: boolean;
}

/**
 * Union-merges `incoming` into a copy of `existing` (per field, deduping values)
 * and reports whether anything changed, so callers can skip a no-op notify.
 */
export function mergeAnalyticsFilters(existing: AnalyticsFilter[], incoming: AnalyticsFilter[]): FilterMergeResult {
    const next = existing.map((filter) => ({ field: filter.field, values: [...filter.values] }));
    let changed = false;
    for (const filter of incoming) {
        const match = next.find((entry) => entry.field === filter.field);
        if (match === undefined) {
            next.push({ field: filter.field, values: [...filter.values] });
            changed = true;
            continue;
        }
        for (const value of filter.values) {
            if (!match.values.includes(value)) {
                match.values.push(value);
                changed = true;
            }
        }
    }
    return { filters: next, changed };
}

/** Order-insensitive equality of two filter sets (field → value-set). */
export function analyticsFiltersEqual(a: AnalyticsFilter[], b: AnalyticsFilter[]): boolean {
    if (a.length !== b.length) return false;
    const index = new Map(a.map((filter) => [filter.field, filter.values]));
    for (const filter of b) {
        const values = index.get(filter.field);
        if (values === undefined || values.length !== filter.values.length) return false;
        const set = new Set(values);
        if (!filter.values.every((value) => set.has(value))) return false;
    }
    return true;
}
